// A stat block, one click from the tracker row.
//
// Reading, nothing else. No button here rolls, targets or writes: the engine
// already does that from the card feed and the effects panel, and a reference
// that might change something is a reference the DM reads warily. Standing rule
// 9 cuts both ways.
//
// The parsing is in state/statBlock.ts and tested there.
import { useEffect, useState } from "react";
import { creatureBlock } from "../state/creatureActions";
import { attackLine, signed, summary, type StatBlock } from "../state/statBlock";
import { runnableActions, type ActionEffect } from "../state/monsterActions";

/** The button that lives on a creature's row. */
export function StatBlockButton({ name, onOpen }: { name: string; onOpen: (name: string) => void }) {
  return (
    <button className="link small statblock-btn" aria-label={`Read ${name}'s stat block`}
      title={`What ${name} can do — traits, actions, reactions`}
      onClick={(e) => { e.stopPropagation(); onOpen(name); }}>&#x1F4D6;</button>
  );
}

/** The panel. Opens with nothing and fills in, because the answer may have to
 *  come from the Roll20 journal and that takes a moment. */
export function StatBlockPanel({ name, onClose, onRun }: { name: string; onClose: () => void; onRun?: (action: ActionEffect) => void }) {
  const [block, setBlock] = useState<StatBlock | null>(null);
  const [state, setState] = useState<"asking" | "ready" | "unknown">("asking");
  const runnable = block ? runnableActions(block.sections) : [];

  useEffect(() => {
    let live = true;
    setState("asking"); setBlock(null);
    creatureBlock(name)
      .then((b) => { if (!live) return; if (b) { setBlock(b); setState("ready"); } else setState("unknown"); })
      .catch(() => { if (live) setState("unknown"); });
    return () => { live = false; };
  }, [name]);

  return (
    <div className="statblock" role="region" aria-label={`${name} stat block`}>
      <div className="row statblock-head">
        <strong>{block?.name || name}</strong>
        <button className="ghost small" onClick={onClose} aria-label="Close the stat block">Close</button>
      </div>

      {state === "asking" && <p className="muted small">Looking for {name}&rsquo;s stat block&hellip;</p>}

      {/* UNKNOWN IS NOT "HAS NOTHING". The block is known for creatures the
          Compass has sent to Roll20; saying why is more use than an empty panel. */}
      {state === "unknown" && (
        <p className="muted small">
          No stat block for {name} yet. The text comes from the creature the Compass sent to Roll20 &mdash;
          send it from the Compass, or open its journal entry in Roll20 once, and it will be here.
        </p>
      )}

      {state === "ready" && block && (
        <>
          {block.hasHeader ? (
            <>
              <p className="statblock-meta">{summary(block)}{block.acNote ? ` (${block.acNote})` : ""}</p>
              {Boolean(block.hpFormula) && <p className="muted small">Hit points {block.hp} ({block.hpFormula})</p>}
              {Boolean(block.speed) && <Line label="Speed" value={block.speed} />}
              <div className="statblock-abilities">
                {block.abilities.map((a) => (
                  <div key={a.key}><dt>{a.label}</dt><dd>{a.score === null ? "—" : a.score} <small>({signed(a.mod)})</small></dd></div>
                ))}
              </div>
              {Boolean(block.saves) && <Line label="Saving throws" value={block.saves} />}
              {Boolean(block.skills) && <Line label="Skills" value={block.skills} />}
              {Boolean(block.resistances) && <Line label="Damage resistances" value={block.resistances} />}
              {Boolean(block.immunities) && <Line label="Damage immunities" value={block.immunities} />}
              {Boolean(block.vulnerabilities) && <Line label="Damage vulnerabilities" value={block.vulnerabilities} />}
              {Boolean(block.conditionImmunities) && <Line label="Condition immunities" value={block.conditionImmunities} />}
              {Boolean(block.senses) && <Line label="Senses" value={block.senses} />}
              {Boolean(block.languages) && <Line label="Languages" value={block.languages} />}
            </>
          ) : (
            <p className="muted small">
              What {block.name} can do. The header &mdash; armour class, hit points, abilities &mdash; needs connector 0.8.210 or newer.
            </p>
          )}

          {/* WHAT THE ENGINE COULD RUN FOR YOU.
              A breath weapon is a shape, a save and some dice -- the same four
              facts a spell gives the engine -- and until now the DM ran it by
              hand while the engine sat there knowing how. Only actions read
              COMPLETELY appear: a half-read one would place a shape and resolve
              nothing, which reads as the feature being broken.
              One click per use, not per target. */}
          {Boolean(onRun) && runnable.length > 0 && (
            <section className="statblock-section runnable">
              <h4>The engine can run these</h4>
              {runnable.map((a) => (
                <div key={a.name} className="apply-hit">
                  <span>
                    <strong>{a.name}</strong>{" "}
                    {a.sizeFt} ft {a.shape}{a.widthFt && a.shape === "line" ? ` × ${a.widthFt} ft` : ""}
                    {a.dc ? ` · DC ${a.dc} ${String(a.save || "").toUpperCase()}` : ""}
                    {a.dice ? ` · ${a.dice} ${a.dmgType}` : ""}
                    {a.condition ? ` · ${a.condition}` : ""}
                    {a.half ? " · half on a save" : ""}
                    {a.recharge ? ` · ${a.recharge}` : ""}
                  </span>
                  <button className="small primary" title={`Place the area and resolve it. Read from: ${a.from}`}
                    onClick={() => onRun?.(a)}>Place</button>
                </div>
              ))}
            </section>
          )}
          {block.sections.map((s) => (
            <section key={s.key} className="statblock-section">
              <h4>{s.title}</h4>
              {s.key === "npcaction-l" && Boolean(block.legendaryNote) && <p className="muted small">{block.legendaryNote}</p>}
              <dl>
                {s.entries.map((e, i) => {
                  const attack = attackLine(e.text);
                  return (
                    <div key={`${e.name}-${i}`}>
                      <dt>{e.name}.</dt>
                      <dd>{attack ? <><em>{attack}</em>{e.text.slice(attack.length)}</> : e.text || <span className="muted">&mdash;</span>}</dd>
                    </div>
                  );
                })}
              </dl>
            </section>
          ))}

          {!block.sections.length && <p className="muted small">The stat block came back with no traits or actions.</p>}
        </>
      )}
    </div>
  );
}

const Line = ({ label, value }: { label: string; value: string }) => (
  <p className="statblock-line"><strong>{label}</strong> {value}</p>
);
